import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ActionCableConsumer } from "frontend/utils/actionCableProvider";
import useFetch from "frontend/utils/useFetch";
import usePost from "frontend/utils/usePost";
import usePatch from "frontend/utils/usePatch";
import useDelete from "frontend/utils/useDelete";
import useRefetchOnVisible from "frontend/utils/useRefetchOnVisible";

import GamesSidebar from "frontend/components/GamesSidebar";
import "./GamesLayout.scss";

const GamesLayout = ({ children, isAuthenticated, isGuest, onSignupClick, sidebarOpen, onCloseSidebar, onChallengesChange }) => {
  const [games, setGames] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [createGameError, setCreateGameError] = useState(null);
  const errorTimeoutRef = useRef(null);
  const history = useHistory();

  const { isFetching, doFetch } = useFetch(
    "/api/v1/games",
    ({ json }) => {
      setGames(json.games || []);
      const newChallenges = json.challenges || [];

      setChallenges(newChallenges);

      const hasPending = newChallenges.some(c => !c.you_are_challenger && c.status === 'pending');
      if (onChallengesChange) onChallengesChange(hasPending);
    }
  );

  const { doPost: doPostGame } = usePost(
    "/api/v1/games",
    ({ response, json }) => {
      if (response.ok) {
        setCreateGameError(null);
        if (json && json.game && json.game.id) {
          history.push(`/games/${json.game.id}`);
        }
        doFetch();
      } else {
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        setCreateGameError((json && json.message) || "Failed to create game.");
        errorTimeoutRef.current = setTimeout(() => setCreateGameError(null), 5000);
      }
    }
  );

  const { doPost: doPostChallenge } = usePost(
    "/api/v1/challenges",
    ({ response, json }) => {
      if (response.ok) {
        doFetch();
      } else {
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        setCreateGameError((json && json.message) || "Failed to send challenge.");
        errorTimeoutRef.current = setTimeout(() => setCreateGameError(null), 5000);
      }
    }
  );

  const { doPatch: doPatchChallenge } = usePatch(
    "/api/v1/challenges",
    ({ response, json }) => {
      if (response.ok) {
        if (json && json.game && json.game.id) {
          history.push(`/games/${json.game.id}`);
        }
        doFetch();
      } else {
        if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
        setCreateGameError((json && json.message) || "Action failed.");
        errorTimeoutRef.current = setTimeout(() => setCreateGameError(null), 5000);
      }
    }
  );

  const { doDelete } = useDelete("/api/v1/games", () => {
    doFetch();
  });

  const handleClearCompleted = () => {
    const completedIds = games.filter(g => g.complete).map(g => g.id);
    completedIds.forEach(id => doDelete(id));
  };

  useEffect(() => {
    if (isAuthenticated) {
      doFetch();
    } else {
      setGames([]);
      setChallenges([]);
      if (onChallengesChange) onChallengesChange(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    return () => { if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current); };
  }, []);

  useRefetchOnVisible(doFetch, isAuthenticated);

  const handleReceivedUpdate = () => {
    if (isFetching) { return; }
    doFetch();
  };

  const handleCreateGame = (aiDifficulty = null) => {
    if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    setCreateGameError(null);

    // Guests can play the AI but not real opponents. Prompt rather than fail --
    // the server's 403 guest-1 is the backstop, not the UX.
    if (!aiDifficulty && isGuest) {
      onSignupClick();
      return;
    }

    if (aiDifficulty) {
      doPostGame({ ai_difficulty: aiDifficulty });
    } else {
      doPostGame();
    }
  };

  const handleSendChallenge = (challengedId) => {
    if (isGuest) {
      onSignupClick();
      return;
    }

    doPostChallenge({ challenged_id: challengedId });
  };

  const handleUpdateChallenge = (challengeId, status) => {
    doPatchChallenge(challengeId, { status });
  };

  const getCurrentGameId = () => {
    const match = window.location.pathname.match(/\/games\/(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  const handleGameSelect = () => {
    if (onCloseSidebar) {
      onCloseSidebar();
    }
  };

  return (
    <div className="games-layout">
      {sidebarOpen && isAuthenticated && (
        <div className="sidebar-overlay" onClick={onCloseSidebar} />
      )}
      {isAuthenticated && (
      <GamesSidebar
        games={games}
        challenges={challenges}
        currentGameId={getCurrentGameId()}
        onCreateGame={handleCreateGame}
        onHideGame={doDelete}
        onClearCompleted={handleClearCompleted}
        onSendChallenge={handleSendChallenge}
        onUpdateChallenge={handleUpdateChallenge}
        isAuthenticated={isAuthenticated}
        isGuest={isGuest}
        onSignupClick={onSignupClick}
        isOpen={sidebarOpen}
        onGameSelect={handleGameSelect}
        createGameError={createGameError}
      />
      )}
      <div className="games-content">
        {children}
      </div>
      {isAuthenticated && (
        <ActionCableConsumer
          channel={{ channel: 'GamesChannel' }}
          onReceived={handleReceivedUpdate}
        />
      )}
    </div>
  );
};

export default GamesLayout;
